import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

const FINGERPRINT_KEY = 'itdukes_fingerprint';
const SESSION_ID_KEY = 'itdukes_session_id';
const ERROR_COOLDOWN_KEY = 'itdukes_error_cooldown';
const COOLDOWN_MS = 5000; // Prevent spam - min 5 seconds between same errors

interface ErrorReportOptions {
  errorType?: string;
  metadata?: Record<string, unknown>;
}

export const useErrorReporting = () => {
  const reportError = useCallback(async (
    error: Error | string,
    options: ErrorReportOptions = {}
  ) => {
    try {
      const message = typeof error === 'string' ? error : error.message;
      const stack = typeof error === 'string' ? undefined : error.stack;

      // Skip logging "Failed to fetch" errors to prevent infinite loops
      // These are typically network issues that can't be logged anyway
      if (message === 'Failed to fetch' || message.includes('NetworkError')) {
        console.warn('Network error occurred, skipping error reporting:', message);
        return;
      }

      // Check cooldown to prevent spam
      const cooldownKey = `${ERROR_COOLDOWN_KEY}_${message}`;
      const lastReported = sessionStorage.getItem(cooldownKey);
      if (lastReported && Date.now() - parseInt(lastReported) < COOLDOWN_MS) {
        return;
      }
      sessionStorage.setItem(cooldownKey, Date.now().toString());

      const fingerprint = localStorage.getItem(FINGERPRINT_KEY);
      const sessionId = sessionStorage.getItem(SESSION_ID_KEY);

      await supabase.functions.invoke('log-client-error', {
        body: {
          fingerprint,
          sessionId,
          errorType: options.errorType || 'MANUAL_REPORT',
          message,
          stack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          metadata: options.metadata || {},
        },
      });
    } catch (logError) {
      // Silently fail - we can't log errors about logging errors
      console.warn('Failed to report error (network issue):', logError);
    }
  }, []);

  // Set up global error handlers
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      reportError(event.error || event.message, {
        errorType: 'RUNTIME_ERROR',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message = event.reason?.message || String(event.reason);
      reportError(message, {
        errorType: 'UNHANDLED_PROMISE_REJECTION',
        metadata: {
          reason: String(event.reason),
        },
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [reportError]);

  return { reportError };
};
